import { BadgeInfo } from '@models/badge.model';
import { FlatInfo } from '@models/flat-info.model';
import { BookingChainService } from '@services/api/booking/booking-chain.service';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { PaginatedResponse } from '@services/api/generic-api-client.service';
import { IApiQueryOptions, IOrderOptions } from '@services/api/models/api-query-params.model';
import { ModelLocalizationService } from '@services/formatter/model-localization.service';
import { BookingStatusService } from '@services/util/booking-status.service';
import { FlatInfoService } from '@services/util/flat-info.service';
import { LogService } from '@services/util/log.service';
import { parseISO } from 'date-fns';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BookingFilter } from '../models/booking-filter.model';
import { BookingRow, PaginatedBooking } from '../models/booking.model';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  constructor(
    private readonly bookingClient: BookingClientService,
    private readonly log: LogService,
    private readonly flatInfoService: FlatInfoService,
    private readonly bookingStatusService: BookingStatusService,
    private readonly localizer: ModelLocalizationService,
    private readonly chainService: BookingChainService
  ) {}

  /**
   * Get paginated bookings with optional sorting and filtering
   * @param page Current page number (1-based)
   * @param itemsPerPage Results per page
   * @param sortField Optional field to sort by
   * @param sortOrder Optional sort direction
   * @param filter Optional filter criteria
   * @returns Observable of paginated bookings
   */
  getBookings(
    page: number = 1,
    itemsPerPage: number = 30,
    sortField: string | null = null,
    sortOrder: 'asc' | 'desc' | null = null,
    filter?: BookingFilter
  ): Observable<PaginatedBooking> {
    this.log.debug('Fetching bookings with params:', { page, itemsPerPage, sortField, sortOrder, filter });

    // Map frontend field names to API field names for sorting
    const fieldMappings: Record<string, string> = {
      checkInDate: 'start_date',
      checkOutDate: 'end_date',
      guestName: 'name',
      status: 'bookingStatus'
    };

    // Define the order object based on sorting parameters
    let orderOptions: IOrderOptions = { start_date: 'desc' }; // Default sorting

    if (sortField && sortOrder) {
      // Use the mapped API field name if available, otherwise use the original
      const apiField = fieldMappings[sortField] || sortField;
      orderOptions = { [apiField]: sortOrder };
      this.log.debug(`Mapping sort field ${sortField} to API field ${apiField}`);
    }

    // Prepare filter objects
    const filterObj: Record<string, any> = {};
    const dateFilterObj: Record<string, any> = {};

    // Apply advanced filter options if provided
    if (filter) {
      // Handle guest name filter
      if (filter.guestName) {
        filterObj['name'] = filter.guestName;
      }

      // Handle booking status ID filter (new dynamic approach)
      if (filter['bookingStatus.id']) {
        filterObj['bookingStatus.id'] = filter['bookingStatus.id'];
        this.log.debug('Applied booking status ID filter:', filter['bookingStatus.id']);
      }

      // Handle date filters - pass IDateFilter objects directly
      if (filter.startDate) {
        dateFilterObj['start_date'] = filter.startDate;
      }

      if (filter.endDate) {
        dateFilterObj['end_date'] = filter.endDate;
      }

      this.log.debug('Prepared filter options:', { filterObj, dateFilterObj });
    }

    // Build query options for the API
    const options: IApiQueryOptions = {
      pagination: {
        page,
        itemsPerPage
      },
      order: orderOptions
    };

    // Only add filters and dateFilters if they have values
    if (Object.keys(filterObj).length > 0) {
      options.filters = filterObj;
    }

    if (Object.keys(dateFilterObj).length > 0) {
      options.dateFilters = dateFilterObj;
    }

    this.log.debug('Applied query options:', options);

    // Use the getItems method from the client service
    return this.bookingClient.getItems('', options).pipe(
      map((response: PaginatedResponse<IBookingModel>) => {
        // Transform API models to UI models
        const transformedBookings = response.items.map((booking) => this.mapToBookingRow(booking));

        return {
          items: transformedBookings,
          totalCount: response.totalItems || 0,
          page,
          itemsPerPage
        };
      }),
      catchError((error) => {
        this.log.error('Error fetching bookings:', error);
        return of({
          items: [],
          totalCount: 0,
          page,
          itemsPerPage
        });
      })
    );
  }

  /**
   * Delete a booking
   * @param id Booking ID to delete
   * @returns Observable<boolean> indicating success/failure
   */
  deleteBooking(id: string): Observable<boolean> {
    this.log.debug('Deleting booking with ID:', id);

    return this.bookingClient.deleteItem(Number(id)).pipe(
      map(() => true),
      catchError((error) => {
        this.log.error('Error deleting booking:', error);
        return of(false);
      })
    );
  }

  /**
   * Get a single booking by ID
   * @param id Booking ID to retrieve
   * @returns Observable of the booking data
   */
  getBookingById(id: string): Observable<BookingRow | null> {
    return this.bookingClient.getItem(Number(id)).pipe(
      map((booking) => this.mapToBookingRow(booking)),
      catchError((error) => {
        this.log.error(`Error fetching booking with ID ${id}:`, error);
        return of(null);
      })
    );
  }

  /**
   * Map API booking model to UI booking row model
   * @param booking API booking model
   * @returns UI booking row model
   */
  private mapToBookingRow(booking: IBookingModel): BookingRow {
    if (!booking) {
      this.log.warn('Attempting to map undefined or null booking model');
      return this.createEmptyBookingRow();
    }

    try {
      const startDate = booking.start_date ? parseISO(booking.start_date) : new Date();
      const endDate = booking.end_date ? parseISO(booking.end_date) : new Date();
      const checkinTime = booking.checkin_time ? parseISO(booking.checkin_time) : undefined;
      const checkoutTime = booking.checkout_time ? parseISO(booking.checkout_time) : undefined;
      const createdAt = booking.created_at ? parseISO(booking.created_at) : undefined;
      const updatedAt = booking.updated_at ? parseISO(booking.updated_at) : undefined;

      const adultCount = booking.adult_occupants || 0;
      const childCount = booking.child_occupants || 0;

      return {
        id: booking.id,
        ['@id']: booking['@id'] || undefined,
        flatInfo: this.getFlatInfo(booking),
        startDate: startDate,
        endDate: endDate,
        guestName: booking.name || '',
        email: booking.email_address,
        phone: booking.phone_number,
        adultCount,
        childCount,
        guestCount: adultCount + childCount,
        status: this.createBookingStatusBadge(booking.bookingStatus),
        statusRaw: booking.bookingStatus?.name || '',
        notes: booking.note,
        checkedIn: booking.checked_in || false,
        checkinTime,
        checkedOut: booking.checked_out || false,
        checkoutTime,
        paid: booking.paid || false,
        price: booking.booking_price,
        currency: booking.currency?.code,
        source: booking.bookingSource?.name,
        reservationCode: booking.reservation_code,
        foreignReservationCode: booking.foreign_reservation_code,
        createdAt,
        updatedAt,
        chain: this.chainService.marksFor(booking)
      };
    } catch (error) {
      this.log.error('Error mapping booking model to row:', error, booking);
      return this.createEmptyBookingRow();
    }
  }

  /**
   * Creates an empty booking row object with default values
   * @returns Empty BookingRow object
   */
  private createEmptyBookingRow(): BookingRow {
    return {
      id: 0,
      flatInfo: new FlatInfo(0, '', '', 'bg-gray-200'),
      startDate: new Date(),
      endDate: new Date(),
      guestName: '',
      adultCount: 0,
      childCount: 0,
      guestCount: 0,
      status: new BadgeInfo(0, 'common.unknown', 'bg-gray-100 text-gray-700', 'pi pi-question-circle'),
      statusRaw: '',
      checkedIn: false,
      checkedOut: false,
      paid: false
    };
  }

  private getFlatInfo(booking: IBookingModel): FlatInfo {
    // Create a default FlatInfo if no flats are available
    const flatInfo =
      booking.flats && booking.flats.length > 0
        ? this.flatInfoService.createFlatInfo(booking.flats[0])
        : new FlatInfo(0, 'Unknown Building', 'Unknown Flat', 'bg-gray-500');

    return flatInfo;
  }

  /**
   * Creates a BadgeInfo object from a booking status model
   * @param status The booking status model from API
   * @returns BadgeInfo object for UI display
   */
  private createBookingStatusBadge(status: any): BadgeInfo {
    if (!status) {
      return new BadgeInfo(0, 'common.unknown', 'bg-gray-100 text-gray-700', 'pi pi-question-circle');
    }

    return new BadgeInfo(
      status.id || 0,
      this.localizer.getBookingStatusKey(status),
      this.bookingStatusService.getStatusColorClasses(status),
      this.bookingStatusService.getStatusIconClasses(status)
    );
  }
}
